// Device list / detail 資料模型（specs 00 §7.2）。
// shared-user mask：subscription / company_id / registered_at 不會出現在 shared 角色的回應。

import { clusterOf } from '../events/eventsLib';
import i18n from '../../i18n';

export interface DeviceOnline {
  state?: string;
  last_seen?: number;
  stale_hint?: boolean;
}

export interface DeviceCapability {
  cluster?: string;
  sub?: string[];
}

export type DeviceClass = 'camera' | 'nvr' | 'dvr' | 'plug' | 'gateway' | string;

export interface DeviceChannel {
	ch: number;
	name?: string;
	enabled: boolean;
	source: 'native' | 'sal' | 'analog' | 'ip' | string;
	online?: boolean;
	model?: string;
	stream_profile?: string;
}

export interface ChannelScope {
	mode: 'all' | 'list' | string;
	channels?: number[];
}

export interface DeviceSummary {
  device_id: string;
  model?: string;
  /** 卡片自訂照片（主人設、雲端同步；2026-10-16）：url = 1 小時簽章網址（帶 v=at）、at = 更新時間 */
  cover?: { url: string; at: number } | null;
  device_class?: DeviceClass;
  channel_count?: number;
  channel_list?: DeviceChannel[];
  /** Caller-owned relation projection; missing on shared NVR/DVR fails closed. */
  channel_scope?: ChannelScope;
  permissions?: string[];
  /** 友善機型名（device_models.display_name，後端衍生；00 §6.1）。 */
  model_name?: string;
  /** 主碼流最高解析度 "WxH"（device_models.max_resolution，後端衍生，唯讀；CAM-83）；缺=機型未登錄。 */
  max_resolution?: string;
  /** owner 設的裝置預設名（device 級）。 */
  display_name?: string;
  /** 呼叫者自己的 per-user 別名（device_user_relations.aliasname）。 */
  aliasname?: string;
  /** 裝置時區 POSIX（common/22；手機攝影機 = 那支手機的時區，ls-loop MW）。 */
  tz?: string;
  /** 呼叫者自己的 per-user 分組（device_user_relations.group；空=未分組，max 32；CAM-24）。 */
  group?: string;
  role?: string;
  company_id?: string | null;
  firmware_version?: string;
  capability_effective?: DeviceCapability[];
  online?: DeviceOnline;
  /**
   * device_models transports (device/09 §3)：裝置支援的本地重配通道，如 'ble' / 'softap'。
   * 目前尚無機型回填此欄；缺/空一律視為無本地通道（見 settings/reconfigDecision.ts）。
   */
  transports?: string[];
  /** common/specs/10_low_power_devices.md §3 draft; optional until backend/common ratifies. */
  standby_state?: 'active' | 'dormant' | 'deep_sleep' | 'offline' | string;
  battery_level?: number;
  battery_state?: 'charging' | 'discharging' | 'full' | 'low' | 'critical' | string;
  external_power?: boolean;
  recording?: { mode?: string; location?: string; quality?: string };
  subscription?: { status?: string; plan?: string; expired_time?: number };
  sd_card?: { present?: boolean; total_mb?: number; used_mb?: number; health?: string };
  firmware_upgrade?: { needs_upgrade?: boolean; latest_version?: string };
  last_event?: { event_id?: string; event_type?: string; event_ts?: number };
  registered_at?: number;
  updated_at?: number;
  /** ls-loop 家用面板：攝影機位置標記（indoor / outdoor / door；空 = 沒標，當室內）。 */
  camera_location?: string;
  /** ls-loop 家用面板裝置（model home-panel）才有，只給主人（docs/panel TP1a.10）。 */
  panel?: { online?: boolean; last_contact_at?: number; photo_count?: number; needs_rejoin?: boolean };
  /** ls-loop IoT 模擬器：模擬裝置（model 以 sim- 開頭；docs/iot-sim/03-sd.md D4）。 */
  simulated?: boolean;
}

/** IoT 模擬器的模擬裝置：名稱旁標「模擬」（RFP N4）；舊雲端沒帶 simulated 時看 model 前綴。 */
export function isSimulated(d: Pick<DeviceSummary, 'model' | 'simulated'>): boolean {
  return d.simulated === true || (d.model ?? '').startsWith('sim-');
}

/** 409 sim_limit（每個帳號同一種模擬裝置最多 6 台）→ i18n key；不是這個錯誤 → null。網頁目前沒有軟體裝置加入入口（TODO），先備好對照。 */
export function simLimitMessageKey(code: string | undefined | null): string | null {
  return code === 'sim_limit' ? 'devices.simLimit' : null;
}

/** 家用面板（model home-panel / device_class panel）。不是攝影機：沒有即時影像、錄影、SD。 */
export function isPanelDevice(d: Pick<DeviceSummary, 'model' | 'device_class'>): boolean {
  return d.model === 'home-panel' || d.device_class === 'panel';
}

/** Tile 三態（16 §4.2）：online 全彩綠點 / stale 黃點提示 / offline 整磚灰階。 */
export type TileState = 'online' | 'stale' | 'offline';
export type StandbyState = 'active' | 'dormant' | 'deep_sleep' | 'offline';

export function standbyState(d: DeviceSummary): StandbyState | null {
  switch (d.standby_state) {
    case 'active':
    case 'dormant':
    case 'deep_sleep':
    case 'offline':
      return d.standby_state;
    default:
      return null;
  }
}

export function tileState(d: DeviceSummary): TileState {
  // 面板的在線看 status 回報（3 分鐘內有聯絡），不走裝置連線
  if (isPanelDevice(d) && d.panel) return d.panel.online ? 'online' : 'offline';
  const standby = standbyState(d);
  if (standby === 'offline') return 'offline';
  if (standby === 'active' || standby === 'dormant' || standby === 'deep_sleep') {
    return d.online?.stale_hint ? 'stale' : 'online';
  }
  if (d.online?.state !== 'online') return 'offline';
  return d.online.stale_hint ? 'stale' : 'online';
}

export function deviceStateLabel(d: DeviceSummary): string {
  if (isPanelDevice(d) && d.panel?.needs_rejoin) return i18n.t('panels.needsRejoin');
  const standby = standbyState(d);
  if (standby && standby !== 'offline') return i18n.t(`devices.standbyState_${standby}`);
  return tileStateLabel(tileState(d));
}

export function batterySummary(d: DeviceSummary): string | null {
  const parts: string[] = [];
  if (typeof d.battery_level === 'number' && Number.isFinite(d.battery_level)) {
    parts.push(`${Math.max(0, Math.min(100, Math.round(d.battery_level)))}%`);
  }
  if (d.external_power === true) parts.push(i18n.t('devices.externalPower'));
  if (d.battery_state) {
    const key = `devices.batteryState_${d.battery_state}`;
    const label = i18n.t(key);
    parts.push(label === key ? d.battery_state : label);
  }
  return parts.length > 0 ? parts.join('・') : null;
}

// Presence 推播(16 §4.2 WSS 腿):signaling 於裝置 sign-in/斷線對 room 廣播
// type:'event' frame,event_id 固定兩值、無 payload(signaling websocket
// buildOnlineEvent;v3-dev 實測同形)。值超出 2^53 → 一律字串比對。
export const ONLINE_EVENT_ID = '0x1100008000000000';
export const OFFLINE_EVENT_ID = '0x1100008000000100';

export type PresenceKind = 'online' | 'offline';

export function presenceOf(eventId: string | undefined): PresenceKind | null {
  const id = eventId?.toLowerCase();
  if (id === ONLINE_EVENT_ID) return 'online';
  if (id === OFFLINE_EVENT_ID) return 'offline';
  return null;
}

/**
 * 16 §4.2 狀態機的 WSS 三條腿:`Stale --> Online: WSS report`、
 * `Online --> Offline: 離線廣播`、`Offline --> Online: 回線`。
 * presence 推播直接翻 state;**非 presence 的任何 room 事件**=該裝置正掛在
 * signaling 上的生命跡象(spec「WSS report」出口)→ 同樣視為 online 並解除
 * stale_hint。REST 快照(online_at 僅 sign-in 時寫)由此獲得即時補位。
 * 回傳 null = 無變化(呼叫端保留原物件參考,避免無謂重渲染)。
 */
export function liveOnlineOf(d: DeviceSummary, eventId: string | undefined, tsUs: number): DeviceOnline | null {
  const state: PresenceKind = presenceOf(eventId) ?? 'online';
  const cur = d.online;
  const lastSeen = Math.max(cur?.last_seen ?? 0, tsUs);
  if (cur?.state === state && cur.stale_hint !== true && (cur.last_seen ?? 0) >= tsUs) return null;
  return { ...cur, state, last_seen: lastSeen, stale_hint: false };
}

/** command:subscribe response 的 point-in-time snapshot。裝置可能在 owner
 * relation 建立前就已上線，該次 ONLINE event 對新 owner 必然不可見；因此 client
 * 晚訂閱時必須先套用 payload.device_status，再接後續 presence event。 */
export function liveOnlineFromStatus(d: DeviceSummary, status: string, tsUs: number): DeviceOnline | null {
  const state = status === 'offline' ? 'offline'
    : status === 'online' || status === 'active' || status === 'dormant' || status === 'deep_sleep'
      ? 'online'
      : null;
  if (!state) return null;
  const cur = d.online;
  const lastSeen = Math.max(cur?.last_seen ?? 0, tsUs);
  if (cur?.state === state && cur.stale_hint !== true && (cur.last_seen ?? 0) >= tsUs) return null;
  return { ...cur, state, last_seen: lastSeen, stale_hint: false };
}

// i18n：原為模組頂層 const map，改成 function 回傳，確保切語言時即時重算
// （頂層求值會凍結在 init 當下語系）。
// stale = snapshot 超過 ~60s（online.stale_hint）；舊文案「狀態可能過期」過於
// 警示、幾乎每張卡都掛時像系統故障 → 改中性「資料可能延遲」（design review P1）。
export function tileStateLabel(st: TileState): string {
  return i18n.t(`devices.tileState_${st}`);
}

/**
 * 向後相容 shim（LiveHubPage 等本任務範圍外的消費端仍以 `TILE_STATE_LABEL[st]`
 * 取值）。用 Proxy 讓索引存取走 tileStateLabel() → 切語言即時重算（不凍結在 init）。
 */
export const TILE_STATE_LABEL = new Proxy({} as Record<TileState, string>, {
  get: (_t, prop: string) => tileStateLabel(prop as TileState),
});

/**
 * capability cluster（16-bit hex,如 '0x8060')→ 使用者可讀功能名。未知 cluster
 * 回 ''（呼叫端過濾隱藏,spec 21 §0「不顯示未知 cluster」)。用於裝置資訊「支援功能」
 * 不再裸秀 raw hex（design review P0）。系統面 cluster（health/streaming）刻意省略。
 */
// i18n：cluster hex → 人話對照。改成「使用點才 i18n.t()」以利切語言重算。
// 已知 cluster 白名單（hex key 是 code、不可動；只有對照「值」是使用者可見字串，
// 翻成 i18n key devices.capCluster_<hex 去 0x>）。
const KNOWN_CAPABILITY_CLUSTERS = new Set<string>([
  '0x8001', '0x8010', '0x8020', '0x8030', '0x8041', '0x8042', '0x8043', '0x8044',
  '0x8050', '0x8060', '0x8070', '0x8071', '0x8072', '0x8080', '0x8081', '0x8090', '0x80a0', '0x80f0',
]);

export function capabilityLabel(cluster: string): string {
  const key = cluster.toLowerCase();
  if (!KNOWN_CAPABILITY_CLUSTERS.has(key)) return '';
  return i18n.t(`devices.capCluster_${key.replace(/^0x/, '')}`);
}

/**
 * 裝置顯示名解析鏈（業主 2026-06-20 定案，00 §6.1）：
 * `aliasname（per-user 自訂）→ display_name（owner 預設）→ model_name（友善機型名）
 * → device_id（DID）`。production DID 不再裸當顯示名；未命名 / emu 裝置因 model_name
 * 退到機型名而非 DID。
 */
export function deviceName(d: DeviceSummary): string {
  return d.aliasname || d.display_name || d.model_name || d.device_id;
}

/**
 * events 頁裝置標籤（裝置篩選 chips 與時間軸列裝置名，17 §4.2）：與 Devices 頁
 * deviceName 同源同權威，採同一解析鏈 `aliasname → display_name → model_name →
 * device_id`（model 為機型代碼，model_name 為其友善名，優先用 model_name）。
 */
export function eventDeviceLabel(
  d: Pick<DeviceSummary, 'device_id' | 'model' | 'model_name' | 'display_name' | 'aliasname'>,
): string {
  return d.aliasname || d.display_name || d.model_name || d.model || d.device_id;
}

/**
 * Camera-class cluster 集合（common/04 §4 總表）：偵測（0x8020 Motion、
 * 0x8041–0x8044 物件/聲音/Tamper）、門鈴（0x8030）、錄影/串流（0x8050/0x80A0）、
 * 攝影機控制（0x8060 PTZ、0x8070 Image）——任一存在即代表裝置有攝影機面。
 * 純感測/系統 cluster 不在列：0x8000 Device Health、0x8010 Sensor Alert（溫濕度）、
 * 0x8001 Plug；燈/喇叭/警報（0x8071/0x8072/0x8080/0x8081）可獨立於攝影機存在，
 * 同樣不作 camera-class 證據。
 */
const CAMERA_CLASS_CLUSTERS = new Set<number>([
  0x8020, 0x8030, 0x8041, 0x8042, 0x8043, 0x8044, 0x8050, 0x8060, 0x8070, 0x80a0, 0x80f0,
]);

export function isChannelDevice(d: DeviceSummary): boolean {
  return d.device_class === 'nvr' || d.device_class === 'dvr';
}

/** Validate and normalize the REST channel directory. Invalid/duplicate rows
 * are dropped; an absent list may be synthesized only from a valid count. */
export function channelDirectory(d: DeviceSummary): DeviceChannel[] {
  if (!isChannelDevice(d)) return [];
  const count = Number.isInteger(d.channel_count) && (d.channel_count ?? 0) > 0 ? Number(d.channel_count) : 0;
  if (count === 0) return [];
  const seen = new Set<number>();
  const rows: DeviceChannel[] = [];
  for (const raw of d.channel_list ?? []) {
    const ch = raw?.ch;
    if (!Number.isInteger(ch) || ch < 1 || ch > count || seen.has(ch)) continue;
    seen.add(ch);
    rows.push({ ...raw, ch, enabled: raw.enabled !== false, source: raw.source || 'native' });
  }
  if (rows.length === 0) {
    for (let ch = 1; ch <= count; ch += 1) rows.push({ ch, enabled: true, source: 'native' });
  }
  return rows.sort((a, b) => a.ch - b.ch);
}

export function canViewChannel(d: DeviceSummary, channel: number): boolean {
  if (!channelDirectory(d).some((c) => c.ch === channel)) return false;
  if (d.role === 'owner' || d.role === 'admin') return true;
  if (d.role !== 'shared-user') return false;
  if (d.channel_scope?.mode === 'all') return true;
  return d.channel_scope?.mode === 'list' && (d.channel_scope.channels ?? []).includes(channel);
}

// 裝置級可控（21 §2.1 permission gate）：owner/admin，或 shared-user 具 `control`
// permission（02 §2.6）。缺 role fail-closed（APP#61）。owner-only 面（§4 display_name、
// §5.2 維運、§6 錄影設定、OTA）另以 isOwner 判，不走此函式。
export function canControlDevice(d: DeviceSummary): boolean {
  if (d.role === 'owner' || d.role === 'admin') return true;
  return (d.permissions ?? []).includes('control');
}

export function canControlChannel(d: DeviceSummary, channel: number): boolean {
  if (!canViewChannel(d, channel)) return false;
  return canControlDevice(d);
}

export function accessibleChannels(d: DeviceSummary): DeviceChannel[] {
  return channelDirectory(d).filter((c) => canViewChannel(d, c.ch));
}

export type CameraClassState = 'camera' | 'non-camera' | 'unknown';

/**
 * Live 入口的 camera-class 判別（16 §3.2 Live tab =「選一台攝影機」；功能渲染只依
 * capability_effective 交集——ADR-034 / 21 §0）。
 *   camera     ：capability_effective 含任一 camera-class cluster（上表）。
 *   non-camera ：有 capability 但全非 camera-class（如 sensor-th 僅 0x8000/0x8010）
 *                → Live 入口隱藏，避免 start_viewing 30s timeout 白等。
 *   unknown    ：capability_effective 缺/空 = cold-start（剛綁定、device 未首次
 *                sign-in 上報，21 §2.4）→ 不得判為非攝影機，UI 應 disable +
 *                「等待裝置首次連線…」。
 */
export function cameraClass(d: DeviceSummary): CameraClassState {
  if (isPanelDevice(d)) return 'non-camera';
  const caps = d.capability_effective;
  if (!caps || caps.length === 0) return 'unknown';
  return caps.some((c) => CAMERA_CLASS_CLUSTERS.has(Number(c.cluster))) ? 'camera' : 'non-camera';
}

/** cameraClass 的布林便捷形（cold-start 'unknown' 不算 camera，也不得當 non-camera 處理）。 */
export function isCameraClass(d: DeviceSummary): boolean {
  return cameraClass(d) === 'camera';
}

// ---- device_class 分組（APP#39）：清單將攝影機與智能裝置(非相機)分組呈現 ----
export type DeviceClassKind = 'camera' | 'smart_iot';

/** 清單分組用類別：非相機(有 capability 但全非 camera-class)=smart_iot;其餘(相機/cold-start)=camera。 */
export function deviceClassKind(d: DeviceSummary): DeviceClassKind {
  return cameraClass(d) === 'non-camera' ? 'smart_iot' : 'camera';
}

export type DeviceIconKind = 'camera' | 'plug' | 'sensor' | 'device' | 'panel';

/** tile 圖示類別：相機→camera;非相機依 capability 判 plug(0x8001)/sensor(0x8010)/泛用 device。 */
export function deviceIconKind(d: DeviceSummary): DeviceIconKind {
  if (isPanelDevice(d)) return 'panel';
  if (cameraClass(d) === 'camera') return 'camera';
  const clusters = new Set((d.capability_effective ?? []).map((c) => (c.cluster ?? '').toLowerCase()));
  if (clusters.has('0x8001')) return 'plug';
  if (clusters.has('0x8010')) return 'sensor';
  return 'device';
}

/** 把裝置分成「攝影機 / 智能裝置」兩組(各自維持輸入順序;攝影機組在前)。純函式。 */
export function groupDevicesByClass<T extends DeviceSummary>(devices: T[]): { kind: DeviceClassKind; devices: T[] }[] {
  const cameras: T[] = [];
  const smart: T[] = [];
  for (const d of devices) (deviceClassKind(d) === 'smart_iot' ? smart : cameras).push(d);
  const out: { kind: DeviceClassKind; devices: T[] }[] = [];
  if (cameras.length) out.push({ kind: 'camera', devices: cameras });
  if (smart.length) out.push({ kind: 'smart_iot', devices: smart });
  return out;
}

/** 21 §2.4 cold-start 占位文案（Live 入口與設定頁共用語彙）。i18n：改 function 即時重算。 */
export function capabilityPendingText(): string {
  return i18n.t('devices.capabilityPending');
}

/**
 * 向後相容 shim（LiveHubPage 等範圍外消費端仍以常數名引用）。改 getter 讓每次
 * 讀取都走 i18n.t() → 切語言即時重算。⚠️ 範圍外檔之後 i18n 化時應改用
 * capabilityPendingText()，本 export 可隨之移除。
 */
export const CAPABILITY_PENDING_TEXT: string = capabilityPendingText();

// i18n：event_type code → 人話，改使用點 i18n.t()（白名單 code 不可動）。
const KNOWN_EVENT_TYPES = new Set<string>(['object_person', 'object_vehicle', 'object_pet', 'motion', 'sound', 'ring']);

export function eventTypeLabel(t: string | undefined): string {
  if (!t) return i18n.t('devices.eventTypeGeneric');
  // WSS room 即時事件帶 capability-encoded hex（如 0x1100008041000001，W3/APP-01）
  // → 以 cluster bits 對照人話標籤（17 §4.2），與 events 時間軸同一規則。
  if (t.startsWith('0x') || t.startsWith('0X')) return clusterOf(t).label;
  return KNOWN_EVENT_TYPES.has(t) ? i18n.t(`devices.eventType_${t}`) : t;
}

// i18n：recording mode code → 人話，改 function 回傳 map 以利切語言重算。
const KNOWN_RECORDING_MODES = ['continuous', 'event', 'off'] as const;

export function recordingModeLabel(mode: string): string {
  return (KNOWN_RECORDING_MODES as readonly string[]).includes(mode)
    ? i18n.t(`devices.recordingMode_${mode}`)
    : mode;
}

/** 訂閱過期/停用判定（僅 owner 看得到 subscription；shared 已被 mask）。 */
export function subscriptionExpired(d: DeviceSummary): boolean {
  const s = d.subscription;
  if (!s?.status) return false;
  return s.status !== 'active' && s.status !== 'trial';
}

/**
 * 縮圖降級 placeholder 的漸層色組索引。
 * 16 §4.1 定版規則：snapshot API 落地前，Tile 主畫面用漸層底色 + 型號 icon，
 * 不留空白、不放破圖、不顯示錯誤字樣。
 */
export function gradientIndex(d: DeviceSummary): number {
  let h = 0;
  for (let i = 0; i < d.device_id.length; i++) h = (h * 31 + d.device_id.charCodeAt(i)) >>> 0;
  return h % 3;
}
